// =============================================================================
// fileInterruptions.js — UNE surface d'interruption à la fois
// (refonte « Mosaïque vivante », lot L6 ; synthèse §6.6).
//
// À l'arrivée, jusqu'à quatre calques s'empilaient (capture mob-ma-semaine :
// l'accueil, la bannière « session sécurisée », le bandeau d'installation et la
// barre d'onglets ; au bureau, la bienvenue par-dessus la demande de
// notifications). Désormais les surfaces DEMANDENT leur tour, et une seule
// s'affiche ; les autres attendent qu'elle soit fermée. Ordre de passage :
//   0 session        la session sécurisée à renouveler (sécurité d'abord)
//   1 bienvenue      la première visite (la tâche)
//   2 cle            proposer Face ID / Touch ID (découverte)
//   3 notifications  demander l'autorisation des notifications (réservé : la
//                    fenêtre du démarrage est retirée le 30/09/2026 ; le lot push
//                    la demandera après un premier acte)
//   4 installation   installer Aramis (Safari : au troisième lancement)
// Pas de préemption : une surface affichée garde la main jusqu'à sa fermeture
// (aucune fenêtre ne disparaît sous les doigts) ; l'ordre départage celles qui
// attendent. Module sans DOM ni React pour le cœur (authGuard.js, hors React,
// s'en sert), plus un crochet React.
// =============================================================================
import { useEffect, useSyncExternalStore } from "react";

export const PRIORITES = Object.freeze({ session: 0, bienvenue: 1, cle: 2, notifications: 3, installation: 4 });

const attente = new Map();   // clé → rang d'arrivée (départage à priorité égale)
const abonnes = new Set();
let courante = null;
let rang = 0;

function elire() {
  if (courante && attente.has(courante)) return;           // pas de préemption
  let meilleure = null;
  for (const [cle, r] of attente) {
    if (meilleure === null) { meilleure = cle; continue; }
    const pa = PRIORITES[cle], pb = PRIORITES[meilleure];
    if (pa < pb || (pa === pb && r < attente.get(meilleure))) meilleure = cle;
  }
  if (meilleure !== courante) {
    courante = meilleure;
    abonnes.forEach((f) => { try { f(); } catch { /* abonné défaillant */ } });
  }
}

/** Une surface veut s'afficher. */
export function demander(cle) {
  if (!(cle in PRIORITES)) throw new Error("fileInterruptions : surface inconnue « " + cle + " »");
  if (!attente.has(cle)) attente.set(cle, rang++);
  elire();
}
/** La surface est fermée (ou n'a plus lieu d'être). */
export function liberer(cle) {
  if (!attente.delete(cle)) return;
  if (courante === cle) courante = null;
  elire();
}
export const surfaceCourante = () => courante;
export function abonner(f) { abonnes.add(f); return () => { abonnes.delete(f); }; }
/** Tests uniquement. */
export function reinitialiserFile() { attente.clear(); courante = null; rang = 0; abonnes.clear(); }

/** Crochet : `voulue` dit si la surface veut s'afficher ; renvoie true quand
 *  c'est son tour. Libère au démontage. */
export function useTourInterruption(cle, voulue) {
  const c = useSyncExternalStore(abonner, surfaceCourante, surfaceCourante);
  useEffect(() => {
    if (voulue) demander(cle); else liberer(cle);
    return () => liberer(cle);
  }, [cle, voulue]);
  return !!voulue && c === cle;
}
